import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import * as api from "@/src/api/sdk.gen";
import type { PersonItem } from "@/src/api/types.gen";

const STATUS_STYLES: Record<string, string> = {
    completed: "bg-emerald-50 text-emerald-700",
    pending: "bg-amber-50 text-amber-700",
    failed: "bg-red-50 text-red-700",
    active: "bg-emerald-50 text-emerald-700",
    expired: "bg-slate-100 text-slate-500",
};

function formatMoney(amount: number, currency: string | null | undefined) {
    if (!currency) return String(amount);
    try {
        return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 2 }).format(
            amount,
        );
    } catch {
        return `${amount.toFixed(2)} ${currency}`;
    }
}

function PersonCard({ person }: { person: PersonItem }) {
    const revenueByCurrency = person.revenueByCurrency || [];
    const enrollments = person.enrollments || [];
    const transactions = person.transactions || [];

    return (
        <div className="rounded-xl border border-slate-200 p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h4 className="text-sm font-bold text-slate-900">{person.email ?? `User #${person.id}`}</h4>
                <div className="flex gap-3 text-xs font-bold text-slate-600">
                    {revenueByCurrency.length === 0 ? (
                        <span className="text-slate-400">No paid transactions</span>
                    ) : (
                        revenueByCurrency.map((entry) => (
                            <span key={entry.currency}>{formatMoney(entry.amount, entry.currency)} paid</span>
                        ))
                    )}
                </div>
            </div>

            {enrollments.length > 0 && (
                <div className="mt-3">
                    <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                        Enrollments
                    </p>
                    <ul className="mt-1 space-y-1">
                        {enrollments.map((enrollment) => (
                            <li key={enrollment.id} className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                                <span
                                    className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                        STATUS_STYLES[enrollment.status] ?? "bg-slate-100 text-slate-600"
                                    }`}
                                >
                                    {enrollment.status}
                                </span>
                                <span className="font-medium text-slate-700">{enrollment.label}</span>
                                <span className="text-slate-400">·</span>
                                {enrollment.courseUuid ? (
                                    <Link
                                        to={`/admin/dashboard/courses/${enrollment.courseUuid}/progress?studentId=${person.id}`}
                                        className="font-medium text-indigo-600 hover:underline"
                                    >
                                        {enrollment.courseTitle} progress →
                                    </Link>
                                ) : (
                                    <span>{enrollment.courseTitle}</span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="mt-3">
                <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                    Transactions ({transactions.length})
                </p>
                {transactions.length === 0 ? (
                    <p className="mt-1 text-xs text-slate-400">No transactions.</p>
                ) : (
                    <div className="mt-1 overflow-x-auto">
                        <table className="w-full text-left text-xs">
                            <tbody className="divide-y divide-slate-100">
                                {transactions.map((tx) => (
                                    <tr key={tx.id}>
                                        <td className="py-1.5 pr-3 font-mono text-slate-500">{tx.checkoutId}</td>
                                        <td className="py-1.5 pr-3 text-slate-600">{tx.planName ?? "—"}</td>
                                        <td className="py-1.5 pr-3 text-slate-600">{tx.provider ?? "—"}</td>
                                        <td className="py-1.5 pr-3 font-bold text-slate-900">
                                            {formatMoney(tx.amount, tx.currency)}
                                            {tx.zeroValueLabel ? (
                                                <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">
                                                    {tx.zeroValueLabel}
                                                </span>
                                            ) : null}
                                        </td>
                                        <td className="py-1.5 pr-3">
                                            <span
                                                className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                                    STATUS_STYLES[tx.status ?? ""] ?? "bg-slate-100 text-slate-600"
                                                }`}
                                            >
                                                {tx.status ?? "unknown"}
                                            </span>
                                        </td>
                                        <td className="py-1.5 text-slate-400">
                                            {tx.createdAt ? new Date(tx.createdAt).toLocaleDateString() : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export function PersonSidebar({ email, onClose }: { email: string | null; onClose: () => void }) {
    const [people, setPeople] = useState<PersonItem[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fetchedForEmail, setFetchedForEmail] = useState<string | null>(null);

    if (email !== null && email !== fetchedForEmail && !loading) {
        setLoading(true);
        setError(null);
        setPeople(null);
    }

    useEffect(() => {
        if (!email) return;

        api.getPaymentsPersonApiV1AdminPaymentsDashboardPersonGet({
            query: { email },
        })
            .then(({ data, error: apiErr }) => {
                if (apiErr || !data) {
                    throw new Error("Lookup failed");
                }
                setPeople(data.people);
            })
            .catch((err) => setError(err instanceof Error ? err.message : "Lookup failed"))
            .finally(() => {
                setFetchedForEmail(email);
                setLoading(false);
            });
    }, [email]);

    if (!email) return null;

    return createPortal(
        <div className="fixed inset-0 z-50 flex justify-end">
            <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="absolute inset-0 bg-slate-900/40"
            />
            <div className="relative flex h-full w-full max-w-2xl flex-col bg-white shadow-xl">
                <div className="flex items-center justify-between border-b border-slate-200 p-4">
                    <h3 className="text-sm font-bold text-slate-900">{email}</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <div className="flex-1 space-y-3 overflow-y-auto p-4">
                    {loading && <div className="h-40 animate-pulse rounded-xl bg-slate-100" />}
                    {error && <p className="text-sm text-red-600">{error}</p>}
                    {!loading && !error && people && people.length === 0 && (
                        <p className="text-sm text-slate-400">No user found for that email.</p>
                    )}
                    {!loading && people?.map((person) => <PersonCard key={person.id} person={person} />)}
                </div>
            </div>
        </div>,
        document.body,
    );
}
